/**
 * Anonymous bootstrap guard utilities for AuthContext.
 *
 * Extracted as pure functions so they can be imported directly by unit tests
 * without loading the full AuthContext module (and its React / RN dependencies).
 *
 * @see contexts/AuthContext.tsx — primary consumer
 */

// ── Auth route exclusion ───────────────────────────────────────────────────────

/**
 * Routes where anon bootstrap must NOT fire. These are sign-in/sign-up surfaces
 * where an auto-minted anon session would immediately redirect the user away.
 */
export const AUTH_ROUTE_PREFIXES = [
  '/signin',
  '/signup',
  '/(auth)',
  '/auth/callback',
  '/reset-password',
  '/verify-email',
];

export function isAuthRoute(pathname: string): boolean {
  return AUTH_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// ── Explicit sign-out intent persistence ──────────────────────────────────────

export const EXPLICIT_SIGNOUT_KEY = 'auth.explicit_signout';
export const EXPLICIT_SIGNOUT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// ── Bootstrap guard ───────────────────────────────────────────────────────────

export interface BootstrapArgs {
  ffEnabled: boolean;
  intentHydrated: boolean;
  sessionLoading: boolean;
  hasBaSession: boolean;
  anonFired: boolean;
  explicitSignedOut: boolean;
  pathname: string;
}

/**
 * Determines whether the anonymous bootstrap should fire.
 * Centralises all guard conditions in one place for testability.
 */
export function shouldBootstrapAnon(args: BootstrapArgs): boolean {
  return (
    args.ffEnabled &&
    args.intentHydrated &&
    !args.sessionLoading &&
    !args.hasBaSession &&
    !args.anonFired &&
    !args.explicitSignedOut &&
    !isAuthRoute(args.pathname)
  );
}
